import { useMemo } from 'react';

import { useTenantPressureMetricsContext } from '../contexts/TenantPressureMetricsContext';
import type {
  TenantPressureMetricsProductSlice,
  TenantPressureMetricsResponse,
} from '../types';

interface UseTenantPressureMetricsOptions {
  enabled?: boolean;
  initialAi?: TenantPressureMetricsProductSlice | null;
  initialReverseIvr?: TenantPressureMetricsProductSlice | null;
}

interface UseTenantPressureMetricsReturn {
  isConnected: boolean;
  isSubscribed: boolean;
  isRefreshing: boolean;
  ai: TenantPressureMetricsProductSlice | null;
  reverseIvr: TenantPressureMetricsProductSlice | null;
  lastEvent: TenantPressureMetricsResponse | null;
  refresh: (reason?: string) => Promise<void>;
}

/**
 * Read the shared tenant pressure metrics store.
 */
export function useTenantPressureMetrics(
  options: UseTenantPressureMetricsOptions = {},
): UseTenantPressureMetricsReturn {
  const {
    enabled = true,
    initialAi = null,
    initialReverseIvr = null,
  } = options;
  const context = useTenantPressureMetricsContext();

  return useMemo(
    () => ({
      isConnected: context.isConnected,
      isSubscribed: enabled ? context.isSubscribed : false,
      isRefreshing: enabled ? context.isRefreshing : false,
      ai: enabled ? (context.ai ?? initialAi) : initialAi,
      reverseIvr: enabled ? (context.reverseIvr ?? initialReverseIvr) : initialReverseIvr,
      lastEvent: enabled ? context.lastPayload : null,
      refresh: context.refresh,
    }),
    [
      context.ai,
      context.isConnected,
      context.isRefreshing,
      context.isSubscribed,
      context.lastPayload,
      context.refresh,
      context.reverseIvr,
      enabled,
      initialAi,
      initialReverseIvr,
    ],
  );
}

export default useTenantPressureMetrics;
